import { DateTime } from 'luxon';
import { telemetry } from '../backend';
import { getCurrentBatteryLevelIfAvailable } from '../battery';
import { hasFeatureEnabled } from '../feature';
import { getCurrentPositionIfAvailable } from '../geo';
import {
  startWatchingOrientation,
  stopWatchingOrientation,
} from '../orientation';
import { captureEvent as captureAnonymousEvent } from './anonymous-data-capture';

const capturePositional = async () => {
  const currentPosition = await getCurrentPositionIfAvailable();
  const { batteryLevel, batteryIsCharging } =
    await getCurrentBatteryLevelIfAvailable();

  await captureAnonymousEvent(
    {
      type: 'scheduled: positional',
      payload: {
        timestamp: DateTime.now().toUTC().toISO(),
        latitude: currentPosition.latitude,
        longitude: currentPosition.longitude,
        speed: currentPosition.speed,
        heading: currentPosition.heading,
        altitude: currentPosition.altitude,
        altitudeAccuracy: currentPosition.altitudeAccuracy,
        accuracy: currentPosition.accuracy,
        batteryLevel,
        batteryIsCharging,
      },
    },
    true,
  );
};

const captureGyroscopic = async ({
  gyroscopeCaptureWindowDuration,
}: {
  gyroscopeCaptureWindowDuration: number;
}) => {
  if (hasFeatureEnabled('data-capture-gyroscope')) {
    telemetry(
      telemetry.DEBUG,
      '[data-capture/gyroscopic] starting to watch orientation',
    );

    // Gyroscopic data capture should run for 1 minute
    startWatchingOrientation(async (data) => {
      if (!(data?.alpha && data?.beta && data?.gamma)) return;
      await captureAnonymousEvent({
        type: 'scheduled: gyroscopic',
        payload: {
          ...data,
          // Not all properties are provided with the spread operator on DeviceOrientationEvents
          alpha: data.alpha,
          beta: data.beta,
          gamma: data.gamma,
          timestamp: DateTime.now().toUTC().toISO(),
        },
      });
    });

    // After 1 minute, send gyroscopic data and positional information
    setTimeout(async () => {
      stopWatchingOrientation();
    }, gyroscopeCaptureWindowDuration);
  }
};

const initialize = async () => {
  const intervalDuration =
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window?.DECKEE_CONFIG?.dataCapture?.scheduledEventIntervalDuration ??
    300000; // Defaults to 5 minutes

  const gyroscopeCaptureWindowDuration =
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window?.DECKEE_CONFIG?.dataCapture?.gyroscopeCaptureWindowDuration ?? 60000; // Defaults to 1 minute

  // This will also capture the users position if available
  capturePositional();
  captureGyroscopic({ gyroscopeCaptureWindowDuration });

  setInterval(async () => {
    await capturePositional();
    await captureGyroscopic({ gyroscopeCaptureWindowDuration });
  }, intervalDuration);
};

export default initialize;
